import type { Route } from './Layout'

/**
 * Whether the user can reach a child route: it has a path and either no guard or a granted one.
 */
const isChildAccessible = (child: Route, hasPermission: (_: string) => boolean): boolean =>
  !!child.path && (!child.guard || hasPermission(child.guard.permission))

/**
 * Returns the children (`childOf`) of `parent` the user can reach, in route order.
 */
export const getAccessibleChildren = (
  parent: Route,
  routes: Route[],
  hasPermission: (_: string) => boolean,
): Route[] =>
  routes.filter(
    route => route.childOf === parent.pathname && isChildAccessible(route, hasPermission),
  )

/**
 * Whether the user can access a top-level route: it holds a granted guard, or — for a SideMenu
 * group without guard nor path — at least one of its children is accessible.
 */
const isParentAccessible = (
  route: Route,
  routes: Route[],
  hasPermission: (_: string) => boolean,
): boolean => {
  if (route.guard) {
    return hasPermission(route.guard.permission)
  }

  return !route.path && getAccessibleChildren(route, routes, hasPermission).length > 0
}

/**
 * Returns the parent routes the user can access (filtered by permission, no
 * `childOf` parent). A group without guard is accessible as soon as one of its children is.
 * Single source of truth for the SideMenu entries and for the
 * guard's fallback redirect (first accessible route).
 */
export const getParentRoutes = (routes: Route[], hasPermission: (_: string) => boolean) => {
  return routes
    .filter(route => isParentAccessible(route, routes, hasPermission))
    .filter(route => !route?.childOf)
}

/**
 * Returns the SideMenu entries the user can access: the accessible parent routes that carry a
 * label (detail routes have none).
 */
export const getNavigationEntries = (routes: Route[], hasPermission: (_: string) => boolean) =>
  getParentRoutes(routes, hasPermission).filter(route => !!route.label)

/**
 * Whether the SideMenu navigation must be hidden: the user's only entry is a route flagged
 * `hideNavigationWhenSoleEntry`.
 */
export const shouldHideNavigation = (
  routes: Route[],
  hasPermission: (_: string) => boolean,
): boolean => {
  const entries = getNavigationEntries(routes, hasPermission)

  return entries.length === 1 && !!entries[0].hideNavigationWhenSoleEntry
}

/**
 * Returns the URL a navigation to `route` should land on: its own `path`, or — for a SideMenu
 * group, which has no `path` — the path of its first child the user can access. Undefined when
 * there is none. Used by the guard's fallback redirect.
 */
export const getRouteTargetPath = (
  route: Route,
  routes: Route[],
  hasPermission: (_: string) => boolean,
): string | undefined => {
  if (route.path) {
    return route.path
  }

  return getAccessibleChildren(route, routes, hasPermission)[0]?.path
}

/**
 * Returns the URL of the first top-level entry the user can actually reach (in route order),
 * skipping a SideMenu group none of whose children is accessible. Undefined when there is none.
 * Target of the guard's fallback redirect.
 */
export const getFirstAccessibleTargetPath = (
  routes: Route[],
  hasPermission: (_: string) => boolean,
): string | undefined =>
  getParentRoutes(routes, hasPermission)
    .map(route => getRouteTargetPath(route, routes, hasPermission))
    .find(Boolean)

/**
 * Resolves the route matching the current URL. Normalises the dynamic `:uuid`
 * segment so parameterised paths match (e.g. `/transfers/abc` → `/transfers/:uuid`).
 *
 * Used by the permission guard to find the permission of the current page.
 */
export const getCurrentRoute = (
  routes: Route[],
  pathname: string,
  uuid?: string,
): Route | undefined => {
  const currentPath = uuid ? pathname.replace(uuid, ':uuid') : pathname

  return routes.find(route => route.path === currentPath)
}
